import {createContext, useContext, useEffect, useMemo, useState} from 'react';
import api from '@/lib/api';

interface User {
    username: string;
    roles: string[];
}

interface AuthCtx {
    user: User | null;
    isReady: boolean;
    login: (u: string, p: string) => Promise<User>;
    logout: () => void;
}

const ID_TOKEN_KEY = 'id_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_KEY = 'auth_user';

function decodeJwt<T = any>(token: string): T | null {
    try {
        const [, payload] = token.split('.');
        const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64 + '==='.slice((base64.length + 3) % 4);
        const json = atob(padded);
        return JSON.parse(json);
    } catch {
        return null;
    }
}

function readStoredUser(): User | null {
    try {
        const raw = localStorage.getItem(USER_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed.username === 'string' && Array.isArray(parsed.roles)) {
            return {username: parsed.username, roles: parsed.roles};
        }
        return null;
    } catch {
        return null;
    }
}

const AuthContext = createContext<AuthCtx>({} as AuthCtx);

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({children}: { children: React.ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [isReady, setIsReady] = useState(false);

    useEffect(() => {
        try {
            const idTok = localStorage.getItem(ID_TOKEN_KEY);
            const refreshTok = localStorage.getItem(REFRESH_TOKEN_KEY);
            const storedUser = readStoredUser();

            if (idTok) {
                api.setTokens(idTok, refreshTok);
                if (storedUser) {
                    setUser(storedUser);
                } else {
                    const payload = decodeJwt<{ sub: string; roles?: string[] }>(idTok);
                    if (payload?.sub) setUser({username: payload.sub, roles: payload.roles ?? []});
                }
            }
        } finally {
            setIsReady(true);
        }

        // Sync across tabs (login/logout/update)
        const onStorage = (_e: StorageEvent) => {
            const idTok = localStorage.getItem(ID_TOKEN_KEY);
            const refreshTok = localStorage.getItem(REFRESH_TOKEN_KEY);
            const storedUser = readStoredUser();
            api.setTokens(idTok, refreshTok);

            if (idTok) {
                if (storedUser) {
                    setUser(storedUser);
                } else {
                    const payload = decodeJwt<{ sub: string; roles?: string[] }>(idTok);
                    setUser(payload?.sub ? {username: payload.sub, roles: payload?.roles ?? []} : null);
                }
            } else {
                setUser(null);
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    const login = async (username: string, password: string) => {
        const {data} = await api.post('/api/v1/auth/login', {email: username, password});

        const idTok: string | null = data?.idToken ?? null;
        const refreshTok: string | null = data?.refreshToken ?? null;

        if (!idTok || !refreshTok) throw new Error('Login response missing tokens');

        const srvUser: User = (data?.user ?? {username: data?.username, roles: data?.roles}) as User;
        const user: User =
            srvUser?.username && Array.isArray(srvUser?.roles)
                ? srvUser
                : (() => {
                    const p = decodeJwt<{ sub: string; roles?: string[] }>(idTok);
                    return {username: p?.sub ?? username, roles: p?.roles ?? []};
                })();

        localStorage.setItem('id_token', idTok);
        localStorage.setItem('refresh_token', refreshTok);
        localStorage.setItem('auth_user', JSON.stringify(user));
        api.setTokens(idTok, refreshTok);
        setUser(user);
        return user;
    };

    const logout = () => {
        localStorage.removeItem('id_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('auth_user');
        api.clearTokens();
        setUser(null);
    };

    const value = useMemo<AuthCtx>(() => ({user, isReady, login, logout}), [user, isReady]);
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
